import { BuildingId, GameState, UpgradeDefinition, BuildingDefinition } from '../types'
import { BUILDING_ORDER, buildings } from '../data/buildings'
import { upgrades } from '../data/upgrades'
import { getBuildingCount } from './buildingSystem'
import { checkUpgradeRequirement } from './upgradeSystem'

export type VisibleBuilding = {
  readonly building: BuildingDefinition
  readonly isUnlocked: boolean
}

export type VisibleUpgrade = {
  readonly upgrade: UpgradeDefinition
}

export const isBuildingUnlocked = (
  state: GameState,
  buildingId: BuildingId
): boolean => {
  const buildingIndex = BUILDING_ORDER.indexOf(buildingId)

  // First building (Coding Assistant) is always unlocked
  if (buildingIndex === 0) {
    return true
  }

  // Other buildings unlock when you own at least 1 of the previous tier
  const previousBuildingId = BUILDING_ORDER[buildingIndex - 1]
  const ownedPrevious = getBuildingCount(state, previousBuildingId)
  return ownedPrevious.gte(1)
}

export const isBuildingVisible = (
  state: GameState,
  buildingId: BuildingId
): boolean => {
  const buildingIndex = BUILDING_ORDER.indexOf(buildingId)

  // First building is always visible
  if (buildingIndex === 0) {
    return true
  }

  // Building is visible if the previous building is unlocked
  const previousBuildingId = BUILDING_ORDER[buildingIndex - 1]
  return isBuildingUnlocked(state, previousBuildingId)
}

export const getVisibleBuildings = (state: GameState): VisibleBuilding[] => {
  const visibleBuildings: VisibleBuilding[] = []

  for (const buildingId of BUILDING_ORDER) {
    const building = buildings.get(buildingId)
    if (building === undefined) continue

    if (isBuildingVisible(state, buildingId)) {
      visibleBuildings.push({
        building,
        isUnlocked: isBuildingUnlocked(state, buildingId),
      })
    }
  }

  return visibleBuildings
}

export const getVisibleUpgrades = (state: GameState): VisibleUpgrade[] => {
  const availableUpgrades: VisibleUpgrade[] = []

  for (const [upgradeId, upgrade] of upgrades) {
    // Skip already purchased
    if (state.upgrades.has(upgradeId)) continue

    // Only include if requirement is met
    const requirementMet = checkUpgradeRequirement(state, upgrade.requirement)
    if (requirementMet) {
      availableUpgrades.push({ upgrade })
    }
  }

  // Sort by cost ascending (cheapest first)
  availableUpgrades.sort((a, b) => a.upgrade.cost.cmp(b.upgrade.cost))

  return availableUpgrades
}
